import React, { useState, useEffect, useRef } from 'react';
import { Zap, Search, ChevronDown } from 'lucide-react';
import { Macro, getMacros } from '../../services/api';

interface MacroSelectorProps {
  /** Called when user selects a macro — passes the rendered content */
  onSelect: (content: string) => void;
  /** Current ticket data for variable substitution */
  customerName?: string;
  customerEmail?: string;
}

/** Replace {variable} placeholders with actual ticket data */
function renderMacro(content: string, vars: Record<string, string>): string {
  return content.replace(/\{(\w+)\}/g, (match, key) => vars[key] || match);
}

export function MacroSelector({ onSelect, customerName, customerEmail }: MacroSelectorProps) {
  const [macros, setMacros] = useState<Macro[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Load macros once
  useEffect(() => {
    getMacros()
      .then(res => setMacros(res.macros || []))
      .catch(() => { /* macros optional */ });
  }, []);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (macros.length === 0) return null;

  const filtered = search
    ? macros.filter(m =>
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.category.toLowerCase().includes(search.toLowerCase())
      )
    : macros;

  // Group by category
  const grouped: Record<string, Macro[]> = {};
  for (const macro of filtered) {
    const cat = macro.category || 'general';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(macro);
  }

  function handleSelect(macro: Macro) {
    const vars: Record<string, string> = {
      customer_name: customerName || 'Customer',
      customer_email: customerEmail || '',
    };
    const rendered = renderMacro(macro.content, vars);
    onSelect(rendered);
    setOpen(false);
    setSearch('');
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors"
        title="Insert macro"
      >
        <Zap size={12} />
        Macros
        <ChevronDown size={10} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 flex flex-col">
          {/* Search */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search macros..."
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>

          {/* Macros list */}
          <div className="overflow-y-auto flex-1 p-1">
            {Object.entries(grouped).map(([category, categoryMacros]) => (
              <div key={category}>
                <p className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  {category}
                </p>
                {categoryMacros.map(macro => (
                  <button
                    key={macro.macro_id}
                    onClick={() => handleSelect(macro)}
                    className="w-full text-left px-2 py-1.5 rounded-md hover:bg-blue-50 transition-colors group"
                  >
                    <p className="text-xs font-medium text-gray-800 group-hover:text-blue-700">{macro.name}</p>
                    <p className="text-[11px] text-gray-500 truncate">{macro.content.slice(0, 80)}...</p>
                  </button>
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="px-2 py-4 text-xs text-gray-400 text-center">No macros found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
